import React, { useState } from "react";
import { projects } from "../constants";
import { HiExternalLink, HiCode } from "react-icons/hi";

const Project = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const handleOpenModal = (project) => {
    setSelectedProject(project);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  return (
    <section
      id="work"
      className="py-28 px-[6vw] md:px-[8vw] lg:px-[15vw] relative bg-[#050414]"
    >
      {/* Section Title */}
      <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
        <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-[#8245ec]">Portfolio Showcase</h2>
        <h3 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">Featured Projects</h3>
        <div className="w-24 h-1.5 bg-gradient-to-r from-[#8245ec] to-purple-400 mx-auto rounded-full"></div>
        <p className="text-gray-400 text-base sm:text-lg">
          Production-ready full-stack systems and front-end applications built with clean architecture, state management, and real-world utility.
        </p>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.map((project) => (
          <div
            key={project.id}
            onClick={() => handleOpenModal(project)}
            className="bg-white/[0.02] border border-white/10 rounded-3xl overflow-hidden cursor-pointer hover:border-purple-500/50 hover:shadow-[0_10px_40px_rgba(130,69,236,0.2)] hover:-translate-y-2 transition-all duration-300 flex flex-col group"
          >
            {/* Image Preview Container */}
            <div className="p-4 overflow-hidden relative">
              <div className="absolute inset-0 bg-[#8245ec]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 rounded-2xl" />
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-52 object-cover rounded-2xl transform group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Content Body */}
            <div className="p-6 flex-1 flex flex-col justify-between">
              <div>
                <h4 className="text-xl font-bold text-white mb-3 group-hover:text-[#8245ec] transition-colors">
                  {project.title}
                </h4>
                <p className="text-gray-400 text-sm line-clamp-3 leading-relaxed mb-4">
                  {project.description}
                </p>
              </div>

              {/* Tags */}
              <div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.slice(0, 4).map((tag, index) => (
                    <span
                      key={index}
                      className="bg-purple-950/60 text-purple-300 border border-purple-500/20 text-xs font-medium rounded-full px-3 py-1"
                    >
                      {tag}
                    </span>
                  ))}
                  {project.tags.length > 4 && (
                    <span className="text-xs text-gray-500 self-center">+{project.tags.length - 4} more</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Container */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="bg-[#0b0724] border border-white/10 rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden relative max-h-[90vh] flex flex-col">
            
            {/* Modal Header Bar */}
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h3 className="text-2xl font-bold text-white">{selectedProject.title}</h3>
              <button
                onClick={handleCloseModal}
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 text-white text-xl font-bold hover:bg-[#8245ec] flex items-center justify-center transition-colors"
              >
                &times;
              </button>
            </div>

            {/* Modal Scrollable Content */}
            <div className="p-6 overflow-y-auto space-y-6">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-64 sm:h-80 object-cover rounded-2xl border border-white/10 shadow-xl"
              />
              
              <div className="space-y-2">
                <h4 className="text-sm uppercase tracking-widest text-[#8245ec] font-bold">Project Overview</h4>
                <p className="text-gray-300 text-base leading-relaxed">
                  {selectedProject.description}
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm uppercase tracking-widest text-[#8245ec] font-bold">Tech Stack & Architecture</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-purple-950/80 border border-purple-500/30 text-purple-300 text-xs font-medium px-3.5 py-1.5 rounded-xl"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-white/10">
                <a
                  href={selectedProject.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center space-x-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white py-3.5 rounded-xl font-semibold transition-all duration-300"
                >
                  <HiCode size={20} />
                  <span>View Repository</span>
                </a>
                <a
                  href={selectedProject.webapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-[#8245ec] to-purple-600 hover:opacity-90 text-white py-3.5 rounded-xl font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(130,69,236,0.4)]"
                >
                  <span>Live Preview</span>
                  <HiExternalLink size={20} />
                </a>
              </div>
            </div>

          </div>
        </div>
      )}
    </section>
  );
};

export default Project;